// utils/tenantHost.js
//
// WHICH TENANT is this app talking to? (decisions log #4 — every tenant lives
// on <subdomain>.<base domain>, nginx routes them all to the same SPA build.)
//
// Two ways the answer is known, in this order:
//
//   1. THE HOSTNAME. On apollo.mitraonecare.com the login page IS Apollo's —
//      nothing typed can override it.
//   2. TYPED AT LOGIN. app.mitraonecare.com is one login page shared by every
//      customer that has no subdomain yet (infra team, 24 Sep 2026: a new
//      tenant starts there the day it is created; its own subdomain follows
//      once the customer commits). There the person types their TENANT ID
//      (TN00012 — every hospital has one from the day it is created, which a
//      subdomain is not) or, once assigned, their subdomain. Both are
//      remembered per browser so the forgot-password / activation pages
//      inherit them. Only a subdomain rides in the header; a tenant id goes
//      to the backend explicitly (login body, ?tenantId=).
//
// The answer rides on every API call as the X-Tenant-Subdomain header
// (hooks/useAxiosAuth.js); the backend resolves it to the tenant and uses it
// as the login's default organisation selection. It SELECTS a login page —
// it grants nothing; credentials still have to match an account at that
// tenant.
//
// Dev servers run on localhost where the hostname says nothing — set
// localStorage.devTenantSubdomain = 'apollo' to impersonate a tenant HOST
// (the locked banner, no organisation field), as opposed to typing one.

const BASE_DOMAIN = (import.meta.env.VITE_TENANT_DOMAIN || 'mitra-one-care.com')
    .trim().toLowerCase();

const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$/;

// Hostnames under the base domain that are the PLATFORM's, not a hospital's:
// app. is the shared login host, sa. the Super Admin console, and the rest
// are reserved the same way on the backend (tenantManagementController
// RESERVED_SUBDOMAINS). On app.mitraonecare.com the label 'app' looked like a
// tenant subdomain and the shared login hid its subdomain field (25 Sep 2026).
const PLATFORM_LABELS = new Set([
    'www', 'app', 'sa', 'api', 'admin', 'superadmin', 'moc', 'meet', 'ai', 'mail',
    'smtp', 'ftp', 'static', 'cdn', 'assets', 'portal', 'login', 'auth',
    'support', 'help', 'docs', 'status', 'minio', 's3',
]);

const TYPED_KEY = 'tenantSubdomain';
const TYPED_ID_KEY = 'tenantIdTyped';
const DEV_HOST_KEY = 'devTenantSubdomain';

/** TN00012 — the id the platform mints for every tenant (utils/idGenerator). */
export const TENANT_ID_RE = /^TN\d{3,}$/;

const valid = (value) => {
    const v = String(value || '').trim().toLowerCase();
    return v && SUBDOMAIN_RE.test(v) ? v : null;
};

const read = (key) => {
    try { return localStorage.getItem(key); } catch { return null; }
};

/** The tenant subdomain the HOSTNAME names, or null on a shared / MOC host. */
export function hostTenantSubdomain() {
    const dev = read(DEV_HOST_KEY);
    if (dev) return valid(dev);
    const host = window.location.hostname.toLowerCase();
    if (!host.endsWith(`.${BASE_DOMAIN}`)) return null;
    const label = host.slice(0, -(BASE_DOMAIN.length + 1));
    if (!label || label.includes('.') || PLATFORM_LABELS.has(label)) return null;
    return valid(label);
}

/** True on <tenant>.<base domain> — the organisation is decided by the URL. */
export function isTenantHost() {
    return hostTenantSubdomain() !== null;
}

/** The organisation id typed on the shared login page, if any (validated). */
export function getTypedTenantSubdomain() {
    return valid(read(TYPED_KEY));
}

/** Remember (or, with an empty value, forget) the typed organisation id. */
export function setTypedTenantSubdomain(value) {
    const v = valid(value);
    try {
        if (v) localStorage.setItem(TYPED_KEY, v);
        else localStorage.removeItem(TYPED_KEY);
    } catch { /* storage unavailable — the header simply is not sent */ }
    return v;
}

/** The Tenant ID typed on the shared login page, if any (validated, upper-case). */
export function getTypedTenantId() {
    const v = String(read(TYPED_ID_KEY) || '').trim().toUpperCase();
    return TENANT_ID_RE.test(v) ? v : null;
}

/** Remember (or, with an empty value, forget) the typed Tenant ID. */
export function setTypedTenantId(value) {
    const v = String(value || '').trim().toUpperCase();
    const id = TENANT_ID_RE.test(v) ? v : null;
    try {
        if (id) localStorage.setItem(TYPED_ID_KEY, id);
        else localStorage.removeItem(TYPED_ID_KEY);
    } catch { /* storage unavailable */ }
    return id;
}

/** The tenant subdomain this app is acting for, or null when none is known. */
export function getTenantSubdomain() {
    return hostTenantSubdomain() || getTypedTenantSubdomain();
}

export default getTenantSubdomain;
